import type { Robot } from '../hooks/useRobots.ts';
import type { MatrixMetric } from './StatusMatrix.tsx';
import { LIVENESS_STATE, STATUS_STATE, level, levelTone, matrixTone, type Tone } from './state.ts';
import { NO_VALUE, duration, pct } from './format.ts';

/* ==========================================================================
   The six readings the fleet is judged on, in one place.

   A metric row in the status matrix is a *definition* — which quantity, which
   thresholds, which words — and the console has to answer it identically
   wherever it is asked. Defining the rows inside each view is how a fleet
   ends up with a 15% battery redline on one screen and a 20% one on the next.
   So the definitions live here, beside the thresholds they use, and every
   surface that reads the fleet as texture reads the same six rows in the same
   order: what it is doing, whether we can reach it, how much charge is left,
   how long since it spoke, how long its identity is good for, and whether it
   is on the books at all.
   ========================================================================== */

const DAY = 86_400_000;

/** Battery: the robot's own reclaim watermark. Low is bad. */
export const BATTERY_WARN = 40;
export const BATTERY_BAD = 15;

/** Telemetry silence: a robot that has not spoken for two minutes is stale,
    for an hour is gone. High is bad. */
export const SEEN_WARN_MS = 120_000;
export const SEEN_BAD_MS = 3_600_000;

/** X.509 identity: 30 days is a job for this month, 7 for today. */
export const CERT_WARN_DAYS = 30;
export const CERT_BAD_DAYS = 7;

function certReading(r: Robot) {
  if (r.cert_not_after == null) return null;
  const days = (new Date(r.cert_not_after).getTime() - Date.now()) / DAY;
  return {
    tone: levelTone(level(days, CERT_WARN_DAYS, CERT_BAD_DAYS)),
    text: `${Math.round(days)}d`,
  };
}

function seenReading(r: Robot) {
  if (r.last_seen == null) return null;
  const age = Date.now() - new Date(r.last_seen).getTime();
  return {
    tone: levelTone(level(age, SEEN_WARN_MS, SEEN_BAD_MS, true)),
    text: duration(age),
  };
}

export const FLEET_METRICS: readonly MatrixMetric<Robot>[] = [
  // Both categorical rows go through `matrixTone`: at 28 columns the question
  // is "where does the texture break", not "what shade of fine is this one".
  // The readout still says the real word.
  {
    key: 'mode',
    label: 'Mode',
    read: (r) => ({
      tone: matrixTone(STATUS_STATE[r.status].tone),
      text: STATUS_STATE[r.status].label,
    }),
  },
  {
    key: 'link',
    label: 'Link',
    read: (r) => ({
      tone: matrixTone(LIVENESS_STATE[r.liveness].tone),
      text: LIVENESS_STATE[r.liveness].label,
      hollow: LIVENESS_STATE[r.liveness].hollow,
    }),
  },
  {
    key: 'batt',
    label: 'Batt',
    read: (r) =>
      r.battery_pct == null
        ? null
        : {
            tone: levelTone(level(r.battery_pct, BATTERY_WARN, BATTERY_BAD)),
            text: pct(r.battery_pct),
          },
  },
  { key: 'seen', label: 'Seen', read: seenReading },
  { key: 'cert', label: 'Cert', read: certReading },
  {
    key: 'reg',
    label: 'Reg',
    read: (r) =>
      r.active
        ? { tone: 'nominal' as Tone, text: 'active' }
        : { tone: 'offline' as Tone, text: 'inactive', hollow: true },
  },
];

/* --------------------------------------------------------------------------
   The exception set.

   An operations page is not a list of robots; it is a list of the robots that
   are not fine. `fleetExceptions` runs the same six definitions above over the
   fleet and keeps only the readings a human has to do something about — which
   is exactly the set that breaks the texture in the matrix, so the two
   surfaces can never disagree about who is in trouble.
   -------------------------------------------------------------------------- */

export interface Reason {
  /** The metric row's label — MODE, LINK, BATT, SEEN, CERT, REG. */
  label: string;
  /** The reading, in words: `Error`, `8%`, `10h 58m`, `8d`. */
  text: string;
  tone: Tone;
  hollow: boolean;
}

export interface FleetException {
  robot: Robot;
  reasons: readonly Reason[];
  /** 3 fault · 2 no data · 1 held · 0 not current. Sorts the list. */
  severity: number;
}

function severityOf(tone: Tone): number {
  switch (tone) {
    case 'error': return 3;
    case 'offline': return 2;
    case 'maintenance': return 1;
    default: return 0;
  }
}

/**
 * Every robot carrying at least one non-nominal reading, worst first.
 *
 * A metric with no reading at all counts: "we have never heard from this
 * robot" is an exception, not a blank. Nothing here is derived beyond the
 * thresholds declared above — the text is the reading itself.
 */
export function fleetExceptions(robots: readonly Robot[]): FleetException[] {
  const out: FleetException[] = [];
  for (const robot of robots) {
    const reasons: Reason[] = [];
    let severity = -1;
    for (const m of FLEET_METRICS) {
      const r = m.read(robot);
      if (r === null) {
        reasons.push({ label: m.label, text: NO_VALUE, tone: 'offline', hollow: true });
        severity = Math.max(severity, severityOf('offline'));
        continue;
      }
      const tone = matrixTone(r.tone);
      if (tone === 'nominal') continue;
      reasons.push({ label: m.label, text: r.text, tone, hollow: r.hollow ?? false });
      severity = Math.max(severity, severityOf(tone));
    }
    if (reasons.length > 0) out.push({ robot, reasons, severity });
  }
  out.sort((a, b) => b.severity - a.severity || b.reasons.length - a.reasons.length);
  return out;
}
